import React, { useState, useMemo, useEffect } from 'react';
import { useQuery } from 'react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../config/api';
import { useAuth } from '../context/AuthContext';
import {
  getMatchScoreBand,
  MATCH_SCORE_BAND_LABELS,
  MatchScoreBadge,
  MatchStatusBadge,
} from '../utils/matchDisplay';
import { FiFilter, FiChevronDown, FiChevronUp, FiUser, FiX, FiRefreshCw, FiZap, FiEye } from 'react-icons/fi';
import { useResizableColumns } from '../hooks/useResizableColumns';
import LoadingButton from '../components/LoadingButton';
import IconButton from '../components/IconButton';
import MoreMenu from '../components/MoreMenu';
import QueryLoadStatus, { QueryLoadingPanel } from '../components/QueryLoadStatus';
import AiMatchProcessingBar from '../components/AiMatchProcessingBar';
import './Matches.css';
import '../components/AiMatchProcessingBar.css';
import { usePageTitle } from '../context/PageChromeContext';
import { useAtsMatchRun } from '../context/AtsMatchRunContext';
import { staleTimes } from '../config/queryKeys';
import { appConfirm } from '../utils/appDialog';
import ListPager from '../components/ListPager';
import { useClientPager } from '../hooks/useClientPager';

const MATCHES_UI_KEY = 'gobunnyy-matches-ui';

function readMatchesUi() {
  try {
    const parsed = JSON.parse(sessionStorage.getItem(MATCHES_UI_KEY) || 'null');
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function JobMatchCard({
  job,
  matches,
  matchCount,
  avgScore,
  expanded,
  onToggle,
  onAiMatch,
  matching,
  anyMatching,
  showViewJob,
  navigate,
  tableRef,
  getColumnProps,
  ResizeHandle,
}) {
  const {
    pageItems: pagedJobMatches,
    pagination: jobMatchPagination,
    setPage: setJobMatchPage,
  } = useClientPager(matches || [], { resetKey: String(job?.id) });

  return (
    <div className={`job-match-card ${matching ? 'is-matching' : ''}`} aria-busy={matching || undefined}>
      <div className="job-match-header" onClick={onToggle}>
        <div>
          <h3>
            {job.company} - {job.title}
          </h3>
          <p className="job-match-info">
            {matchCount} candidate{matchCount !== 1 ? 's' : ''} matched
            {avgScore > 0 && (
              <>
                {' '}
                • Avg Score: <MatchScoreBadge score={avgScore} />
              </>
            )}
            {job.job_classification_name && ` • Classification: ${job.job_classification_name}`}
            {matching && <span className="job-match-processing-label"> · Scoring…</span>}
          </p>
        </div>
        <div className="job-match-actions">
          <LoadingButton
            className="btn btn-sm btn-primary"
            icon={FiZap}
            loading={matching}
            disabled={anyMatching && !matching}
            onClick={(e) => {
              e.stopPropagation();
              onAiMatch(job.id, job.title);
            }}
            title="AI match candidates to this job"
            loadingLabel="AI matching…"
          >
            AI match
          </LoadingButton>
          {showViewJob && (
            <button
              type="button"
              className="btn btn-sm btn-secondary"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/jobs/${job.id}`);
              }}
            >
              View Job
            </button>
          )}
          {expanded ? <FiChevronUp aria-hidden /> : <FiChevronDown aria-hidden />}
        </div>
      </div>
      {expanded && (
        <div className={`job-match-candidates ${matching ? 'is-dimmed' : ''}`}>
          {matches.length > 0 ? (
            <table ref={tableRef} className="matches-table" style={{ tableLayout: 'fixed', width: '100%' }}>
              <thead>
                <tr>
                  <th {...getColumnProps(0)}>
                    Candidate
                    <ResizeHandle index={0} />
                  </th>
                  <th {...getColumnProps(1)}>
                    Classification
                    <ResizeHandle index={1} />
                  </th>
                  <th {...getColumnProps(2)}>
                    Match Score
                    <ResizeHandle index={2} />
                  </th>
                  <th {...getColumnProps(3)}>
                    Status
                    <ResizeHandle index={3} />
                  </th>
                  <th {...getColumnProps(4)}>
                    Actions
                    <ResizeHandle index={4} />
                  </th>
                </tr>
              </thead>
              <tbody>
                {pagedJobMatches.map((match) => (
                  <tr key={match.id}>
                    <td>
                      <div className="candidate-cell">
                        <FiUser aria-hidden />
                        <strong>
                          {match.first_name} {match.last_name}
                        </strong>
                        <span className="candidate-email">{match.email}</span>
                      </div>
                    </td>
                    <td>{match.current_job_title || 'N/A'}</td>
                    <td>
                      <MatchScoreBadge score={match.match_score} />
                    </td>
                    <td>
                      <MatchStatusBadge status={match.status} />
                    </td>
                    <td>
                      <MoreMenu
                        label={`Actions for ${match.first_name} ${match.last_name}`}
                        size="sm"
                        items={[
                          {
                            id: 'view',
                            label: 'View Profile',
                            icon: FiEye,
                            onSelect: () => navigate(`/candidates/${match.candidate_id}`),
                          },
                        ]}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="empty-state">No candidates matched</div>
          )}
          <ListPager
            page={jobMatchPagination.page}
            pageCount={jobMatchPagination.pageCount}
            total={jobMatchPagination.total}
            limit={jobMatchPagination.limit}
            onPageChange={setJobMatchPage}
          />
        </div>
      )}
    </div>
  );
}

const Matches = () => {
  const { user } = useAuth();
  usePageTitle('ATS match');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { anyMatching, isJobMatching, startJobMatch } = useAtsMatchRun();
  const savedUi = readMatchesUi();
  const [groupBy, setGroupBy] = useState(savedUi.groupBy || 'none');
  const [filterJob, setFilterJob] = useState(savedUi.filterJob || '');
  const [filterCandidate, setFilterCandidate] = useState(savedUi.filterCandidate || '');
  const [filterClassification, setFilterClassification] = useState(savedUi.filterClassification || '');
  const [filterScoreMin, setFilterScoreMin] = useState(savedUi.filterScoreMin || '');
  const [filterStatus, setFilterStatus] = useState(savedUi.filterStatus || '');
  const [expandedJobs, setExpandedJobs] = useState(() => new Set(savedUi.expandedJobs || []));
  const [showFilters, setShowFilters] = useState(Boolean(savedUi.showFilters));

  // Deep-link from Candidates list: /matches?candidate=NameOrId
  useEffect(() => {
    const q = searchParams.get('candidate');
    if (q) {
      setFilterCandidate(q);
      setShowFilters(true);
    }
  }, [searchParams]);

  useEffect(() => {
    try {
      sessionStorage.setItem(
        MATCHES_UI_KEY,
        JSON.stringify({
          groupBy,
          filterJob,
          filterCandidate,
          filterClassification,
          filterScoreMin,
          filterStatus,
          showFilters,
          expandedJobs: [...expandedJobs],
        })
      );
    } catch {
      /* ignore */
    }
  }, [
    groupBy,
    filterJob,
    filterCandidate,
    filterClassification,
    filterScoreMin,
    filterStatus,
    showFilters,
    expandedJobs,
  ]);

  const {
    data: matchesData,
    isLoading: isLoadingMatches,
    refetch: refetchMatches,
    isFetching: isFetchingMatches,
  } = useQuery(
    ['all-matches', user?.role, user?.id],
    async () => {
      if (user?.role === 'candidate') {
        const response = await api.get(`/matches/candidate/${user.id}`);
        return response.data;
      }
      const response = await api.get('/matches');
      return response.data;
    },
    {
      enabled: Boolean(user?.id),
      keepPreviousData: true,
      staleTime: staleTimes.lists,
      cacheTime: anyMatching ? Infinity : 5 * 60 * 1000,
      refetchOnMount: anyMatching ? false : true,
    }
  );

  const { data: jobRoles = [] } = useQuery(['job-roles'], () =>
    api.get('/job-roles').then((res) => res.data)
  );

  const handleAiMatch = async (jobId, jobTitle) => {
    const label = jobTitle ? `"${jobTitle}"` : 'this job';
    if (
      await appConfirm(
        `Run AI matching for ${label}? This uses your AI settings (ATS match provider) to score each candidate profile against the job.`
      )
    ) {
      try {
        await startJobMatch(jobId, { label: jobTitle });
      } catch {
        /* provider already alerts */
      }
    }
  };

  const { getColumnProps, ResizeHandle, tableRef } = useResizableColumns(
    [250, 200, 120, 100, 120],
    'matches-column-widths'
  );

  const processedMatches = useMemo(() => {
    if (!matchesData) return [];

    if (user?.role === 'candidate') {
      return matchesData.filter((match) => {
        if (filterJob && !match.title?.toLowerCase().includes(filterJob.toLowerCase())) return false;
        if (filterScoreMin && match.match_score < parseFloat(filterScoreMin)) return false;
        if (filterStatus && match.status !== filterStatus) return false;
        return true;
      });
    }

    let processed = matchesData
      .map(({ job, matches }) => {
        const filteredMatches = matches.filter((match) => {
          const name = `${match.first_name} ${match.last_name}`.toLowerCase();
          const candQ = filterCandidate.toLowerCase();
          if (
            filterCandidate &&
            !name.includes(candQ) &&
            String(match.candidate_id) !== filterCandidate &&
            !String(match.email || '')
              .toLowerCase()
              .includes(candQ)
          ) {
            return false;
          }
          if (filterClassification && job.job_classification_name !== filterClassification) return false;
          if (filterScoreMin && match.match_score < parseFloat(filterScoreMin)) return false;
          if (filterStatus && match.status !== filterStatus) return false;
          return true;
        });

        return {
          job,
          matches: filteredMatches,
          matchCount: filteredMatches.length,
          avgScore:
            filteredMatches.length > 0
              ? (
                  filteredMatches.reduce((sum, m) => sum + (m.match_score || 0), 0) /
                  filteredMatches.length
                ).toFixed(1)
              : 0,
        };
      })
      .filter((item) => {
        if (filterJob && !item.job.title?.toLowerCase().includes(filterJob.toLowerCase())) {
          if (!item.job.company?.toLowerCase().includes(filterJob.toLowerCase())) return false;
        }
        return item.matchCount > 0 || !filterJob;
      });

    if (groupBy === 'classification') {
      const grouped = {};
      processed.forEach((item) => {
        const key = item.job.job_classification_name || 'Unclassified';
        if (!grouped[key]) grouped[key] = [];
        grouped[key].push(item);
      });
      return Object.entries(grouped).map(([classification, items]) => ({
        classification,
        items,
        totalMatches: items.reduce((sum, item) => sum + item.matchCount, 0),
      }));
    }

    if (groupBy === 'score') {
      const grouped = { high: [], good: [], fair: [], low: [] };
      processed.forEach((item) => {
        grouped[getMatchScoreBand(item.avgScore)].push(item);
      });
      return Object.entries(grouped)
        .filter(([, items]) => items.length > 0)
        .map(([range, items]) => ({
          range,
          items,
          totalMatches: items.reduce((sum, item) => sum + item.matchCount, 0),
        }));
    }

    return processed;
  }, [
    matchesData,
    filterJob,
    filterCandidate,
    filterClassification,
    filterScoreMin,
    filterStatus,
    groupBy,
    user?.role,
  ]);

  const {
    pageItems: pagedMatches,
    pagination: matchPagination,
    setPage: setMatchPage,
  } = useClientPager(processedMatches, {
    resetKey: `${groupBy}|${filterJob}|${filterCandidate}|${filterClassification}|${filterScoreMin}|${filterStatus}|${user?.role || ''}`,
  });

  const toggleJobExpanded = (jobId) => {
    setExpandedJobs((prev) => {
      const next = new Set(prev);
      if (next.has(jobId)) next.delete(jobId);
      else next.add(jobId);
      return next;
    });
  };

  const listBusy = isFetchingMatches || anyMatching;

  const matchesStillLoading = Boolean(user?.id) && isLoadingMatches && matchesData == null;

  const cardProps = (item) => ({
    ...item,
    expanded: expandedJobs.has(item.job.id),
    onToggle: () => toggleJobExpanded(item.job.id),
    onAiMatch: handleAiMatch,
    matching: isJobMatching(item.job.id),
    anyMatching,
    navigate,
    tableRef,
    getColumnProps,
    ResizeHandle,
  });

  if (matchesStillLoading) {
    return (
      <QueryLoadingPanel
        isLoading={isLoadingMatches}
        isFetching={isFetchingMatches}
        hasData={false}
        initialLabel="Loading matches…"
      />
    );
  }

  return (
    <div
      className={`matches-page list-page ${anyMatching ? 'is-ai-matching' : ''}`}
      aria-busy={listBusy && matchesData != null}
    >
      <div className="page-header">
        <div className="page-header-text">
          <h1>ATS match</h1>
          <p className="page-subtitle">ATS match pipeline</p>
        </div>
        <div className="list-page-header-actions page-toolbar">
          <QueryLoadStatus
            isLoading={isLoadingMatches}
            isFetching={isFetchingMatches || anyMatching}
            hasData={matchesData != null}
          />
          <IconButton
            icon={FiFilter}
            label={showFilters ? 'Hide filters' : 'Show filters'}
            active={showFilters}
            onClick={() => setShowFilters(!showFilters)}
          />
          {user?.role !== 'candidate' && (
            <IconButton
              icon={FiRefreshCw}
              label={anyMatching ? 'Matching in progress…' : 'Refresh matches'}
              variant="primary"
              onClick={() => refetchMatches()}
              loading={listBusy}
              disabled={anyMatching}
            />
          )}
        </div>
      </div>

      <AiMatchProcessingBar
        matching={anyMatching}
        refreshing={isFetchingMatches && !anyMatching && matchesData != null}
        label={
          anyMatching
            ? 'AI matching in progress — scoring candidates against the job…'
            : undefined
        }
      />

      {showFilters && (
        <div className="list-filters-panel list-toolbar-panel">
          <div className="filter-row">
            <div className="filter-group">
              <label>Group By</label>
              <select value={groupBy} onChange={(e) => setGroupBy(e.target.value)}>
                <option value="none">No Grouping</option>
                <option value="classification">Job Classification</option>
                <option value="score">Match Score Range</option>
              </select>
            </div>
            {user?.role !== 'candidate' && (
              <>
                <div className="filter-group">
                  <label>Job/Company</label>
                  <input
                    type="text"
                    placeholder="Search jobs or companies..."
                    value={filterJob}
                    onChange={(e) => setFilterJob(e.target.value)}
                  />
                </div>
                <div className="filter-group">
                  <label>Candidate</label>
                  <input
                    type="text"
                    placeholder="Search candidates..."
                    value={filterCandidate}
                    onChange={(e) => setFilterCandidate(e.target.value)}
                  />
                </div>
                <div className="filter-group">
                  <label>Classification</label>
                  <select
                    value={filterClassification}
                    onChange={(e) => setFilterClassification(e.target.value)}
                  >
                    <option value="">All Classifications</option>
                    {jobRoles
                      .filter((role) => role.is_active === 1 || role.is_active === true)
                      .map((role) => (
                        <option key={role.id} value={role.name}>
                          {role.name}
                        </option>
                      ))}
                  </select>
                </div>
              </>
            )}
            <div className="filter-group">
              <label>Min Score</label>
              <input
                type="number"
                placeholder="e.g., 50"
                min="0"
                max="100"
                value={filterScoreMin}
                onChange={(e) => setFilterScoreMin(e.target.value)}
              />
            </div>
            <div className="filter-group">
              <label>Status</label>
              <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
                <option value="">All Statuses</option>
                <option value="pending">Pending</option>
                <option value="reviewed">Reviewed</option>
                <option value="shortlisted">Shortlisted</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>
            {(filterJob ||
              filterCandidate ||
              filterClassification ||
              filterScoreMin ||
              filterStatus) && (
              <IconButton
                icon={FiX}
                label="Clear filters"
                onClick={() => {
                  setFilterJob('');
                  setFilterCandidate('');
                  setFilterClassification('');
                  setFilterScoreMin('');
                  setFilterStatus('');
                }}
              />
            )}
          </div>
        </div>
      )}

      <div className="matches-content">
        {user?.role === 'candidate' ? (
          <div className="matches-list">
            {processedMatches && processedMatches.length > 0 ? (
              pagedMatches.map((match) => (
                <div key={match.id} className="match-card">
                  <div className="match-header">
                    <div>
                      <h3
                        onClick={() => navigate(`/jobs/${match.job_id}`)}
                        style={{ cursor: 'pointer' }}
                      >
                        {match.title || match.job_title}
                      </h3>
                      <p className="match-company">{match.company}</p>
                    </div>
                    <div className="match-score">
                      <MatchScoreBadge score={match.match_score} suffix="% Match" />
                    </div>
                  </div>
                  {match.location && <p className="match-location">{match.location}</p>}
                  {match.status && (
                    <p className="match-status">
                      Status: <MatchStatusBadge status={match.status} />
                    </p>
                  )}
                </div>
              ))
            ) : (
              <div className="empty-state">No matches found</div>
            )}
          </div>
        ) : (
          <div className="matches-by-job">
            {groupBy === 'classification' ? (
              pagedMatches.map((group) => (
                <div key={group.classification} className="match-group">
                  <div className="group-header">
                    <h2>{group.classification}</h2>
                    <span className="group-count">{group.totalMatches} matches</span>
                  </div>
                  <div className="group-items">
                    {group.items.map((item) => (
                      <JobMatchCard key={item.job.id} {...cardProps(item)} showViewJob={false} />
                    ))}
                  </div>
                </div>
              ))
            ) : groupBy === 'score' ? (
              pagedMatches.map((group) => (
                <div key={group.range} className="match-group">
                  <div className="group-header">
                    <h2>{MATCH_SCORE_BAND_LABELS[group.range] || group.range}</h2>
                    <span className="group-count">{group.totalMatches} matches</span>
                  </div>
                  <div className="group-items">
                    {group.items.map((item) => (
                      <JobMatchCard key={item.job.id} {...cardProps(item)} showViewJob={false} />
                    ))}
                  </div>
                </div>
              ))
            ) : processedMatches && processedMatches.length > 0 ? (
              pagedMatches.map((item) => (
                <JobMatchCard key={item.job.id} {...cardProps(item)} showViewJob />
              ))
            ) : (
              <div className="empty-state">No matches found</div>
            )}
          </div>
        )}
      </div>
      <ListPager
        page={matchPagination.page}
        pageCount={matchPagination.pageCount}
        total={matchPagination.total}
        limit={matchPagination.limit}
        onPageChange={setMatchPage}
      />
    </div>
  );
};

export default Matches;
